import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import { VerifiedOutlined as VerifiedOutlinedIcon } from '@mui/icons-material';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../../components/inputs/textInput';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import { COUNTRY_TO_FLAG } from '../../../utils/languageUtils';
import { getLanguageOptions, getSafetyCertificateOptions } from './driverFormConstants';

interface DriverFormFieldsProps<T extends Record<string, any>> {
  control: Control<T>;
  errors: FieldErrors<T>;
  onSubmit: () => void;
  readOnly?: boolean;
}

const styles = {
  sectionCard: {
    mb: 2,
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    borderRadius: 1.5,
    border: (theme: any) => theme.palette.mode === 'light' ? '1px solid' : 'none',
    borderColor: (theme: any) => theme.palette.mode === 'light' ? 'border.light' : 'transparent',
  },
  cardContent: {
    '&:last-child': { pb: 2 },
  },
  sectionTitle: {
    fontSize: (theme: any) => theme.typography.fontSizes.subheading,
    fontWeight: 600,
    mb: 2,
    color: 'text.dark',
  },
  certChipIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: (theme: any) => theme.typography.pxToRem(16),
    opacity: 0.9,
  },
  errorText: {
    display: 'block',
    mt: 0.5,
    ml: 1.75,
  },
};

export const DriverFormFields = <T extends Record<string, any>>({
  control,
  errors,
  onSubmit,
  readOnly = false
}: DriverFormFieldsProps<T>) => {
  const { t } = useTranslation();
  const languageOptions = getLanguageOptions(t);
  const safetyCertificateOptions = getSafetyCertificateOptions(t);

  return (
    <>
      {/* Personal Information */}
      <Card sx={styles.sectionCard}>
        <CardContent sx={styles.cardContent}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            👤 {t('Personal Information')}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Controller
              name={'first_name' as any}
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label={t('First Name')}
                  required
                  fullWidth
                  disabled={readOnly}
                  onKeyDown={e => e.key === 'Enter' && onSubmit()}
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message as any}
                  sx={{ flex: '1 1 200px', minWidth: '200px' }}
                  size="small"
                  variant="outlined"
                />
              )}
            />

            <Controller
              name={'last_name' as any}
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label={t('Last Name')}
                  required
                  fullWidth
                  disabled={readOnly}
                  onKeyDown={e => e.key === 'Enter' && onSubmit()}
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message as any}
                  sx={{ flex: '1 1 200px', minWidth: '200px' }}
                  size="small"
                  variant="outlined"
                />
              )}
            />
          </Box>

          <Controller
            name={'email' as any}
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label={t('Email')}
                type="email"
                fullWidth
                disabled={readOnly}
                onKeyDown={e => e.key === 'Enter' && onSubmit()}
                error={!!errors.email}
                helperText={errors.email?.message as any}
                sx={{ mt: 2 }}
                size="small"
                variant="outlined"
              />
            )}
          />

          <Controller
            name={'base_country' as any}
            control={control}
            render={({ field, fieldState }) => {
              const countryOptions = Object.values(CountryCode).map((code) => ({
                label: COUNTRY_NAMES[code],
                value: code,
                flag: COUNTRY_TO_FLAG[code] || ''
              }));

              return (
                <Box sx={{ mt: 2 }}>
                  <Autocomplete
                    options={countryOptions}
                    disabled={readOnly}
                    getOptionLabel={(option) => option.label}
                    value={field.value ? countryOptions.find(opt => opt.value === field.value) || null : null}
                    onChange={(e, newValue) => field.onChange(newValue?.value || '')}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.flag}</span>
                        <span>{option.label}</span>
                      </Box>
                    )}
                    slotProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [
                          {
                            name: 'flip',
                            enabled: false,
                          },
                          {
                            name: 'preventOverflow',
                            enabled: true,
                          },
                        ],
                      },
                    }}
                    renderInput={(params) => {
                      const selectedOption = field.value ? countryOptions.find(opt => opt.value === field.value) : null;
                      return (
                        <TextInput
                          {...params}
                          label={t('Base Country')}
                          required
                          variant="outlined"
                          size="small"
                          error={!!fieldState.error}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedOption?.flag ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                <span>{selectedOption.flag}</span>
                              </Box>
                            ) : null,
                          }}
                        />
                      );
                    }}
                  />
                  {fieldState.error && (
                    <Typography variant="caption" color="error" sx={styles.errorText}>
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        </CardContent>
      </Card>

      {/* Location Information */}
      <Card sx={styles.sectionCard}>
        <CardContent sx={styles.cardContent}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            📍 {t('Main Location')}
          </Typography>

          <Controller
            name={'coordinates' as any}
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label={t('Coordinates')}
                required
                placeholder="54.72099932613791, 25.228503570972695"
                fullWidth
                disabled={readOnly}
                onKeyDown={e => e.key === 'Enter' && onSubmit()}
                error={!!errors.coordinates}
                helperText={(errors.coordinates?.message as any) || t('Enter coordinates as: latitude, longitude')}
                size="small"
                variant="outlined"
              />
            )}
          />
        </CardContent>
      </Card>

      {/* Languages */}
      <Card sx={styles.sectionCard}>
        <CardContent sx={styles.cardContent}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            🌐 {t('Known Languages')}
          </Typography>

          <Controller
            name={'known_languages' as any}
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={readOnly}
                options={languageOptions}
                getOptionLabel={(option) => option.label}
                value={languageOptions.filter(opt => ((field.value || []) as any).includes(opt.value))}
                onChange={(e, newValue) => {
                  field.onChange(newValue.map(opt => opt.value));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{option.flag}</span>
                    <span>{option.label}</span>
                  </Box>
                )}
                renderTags={(value, getTagProps) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{option.flag}</span>
                            <span>{option.label}</span>
                          </Box>
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                      },
                    ],
                  },
                }}
                renderInput={(params) => (
                  <TextInput
                    {...params}
                    label={t('Languages')}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            )}
          />
        </CardContent>
      </Card>

      {/* Safety Certificates — same Autocomplete + chip pattern as Known Languages */}
      <Card sx={styles.sectionCard}>
        <CardContent sx={styles.cardContent}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            🛡️ {t('Safety Certificates')}
          </Typography>

          <Controller
            name={'safety_certificates' as any}
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={readOnly}
                options={safetyCertificateOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={safetyCertificateOptions.filter(opt =>((field.value || []) as string[]).includes(opt.value))}
                onChange={(e, newValue) => {
                  field.onChange(newValue.map(opt => opt.value));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedOutlinedIcon sx={{ fontSize: 18, opacity: 0.85 }} color="action" />
                    <span>{option.label}</span>
                  </Box>
                )}
                renderTags={(value, getTagProps) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box component="span" sx={styles.certChipIcon} aria-hidden>
                              <VerifiedOutlinedIcon sx={{ fontSize: 16 }} color="action" />
                            </Box>
                            <span>{option.label}</span>
                          </Box>
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      { name: 'flip', enabled: false },
                      { name: 'preventOverflow', enabled: true },
                    ],
                  },
                }}
                renderInput={(params) => (
                  <TextInput
                    {...params}
                    label={t('Certificates')}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            )}
          />
        </CardContent>
      </Card>
    </>
  );
};
